import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { APIListWrapper } from '../../models/common/api-list-wrapper.model';
import { APIResponseWrapper } from '../../models/common/api-response-wrapper';
import { DeliveryRateCard } from '../../contracts/models/delivery-rate-card.model';
import {
  DeliveryRateCardFilters,
  DeliveryRateCardImportBatch,
  DeliveryRateCardImportPreview,
  DeliveryRateCardImportProcessResult,
  DeliveryRateCardImportRow
} from './delivery-rate-card-workspace.model';

@Injectable({ providedIn: 'root' })
export class DeliveryRateCardWorkspaceService {
  private readonly baseUrl = `${environment.restApiUrl}delivery-rate-cards`;

  constructor(private http: HttpClient) {}

  list(filters: DeliveryRateCardFilters): Observable<APIListWrapper<DeliveryRateCard>> {
    let params = new HttpParams();
    Object.entries(filters || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, String(value));
      }
    });
    return this.http.get<APIListWrapper<DeliveryRateCard>>(this.baseUrl, { params });
  }

  get(rateId: number): Observable<APIResponseWrapper<DeliveryRateCard>> {
    return this.http.get<APIResponseWrapper<DeliveryRateCard>>(`${this.baseUrl}/${rateId}`);
  }

  create(contractId: number, revisionId: number, rateCard: DeliveryRateCard): Observable<APIResponseWrapper<DeliveryRateCard>> {
    return this.http.post<APIResponseWrapper<DeliveryRateCard>>(
      `${this.baseUrl}/contracts/${contractId}/revisions/${revisionId}`,
      rateCard
    );
  }

  update(rateId: number, contractId: number, revisionId: number, rateCard: DeliveryRateCard): Observable<APIResponseWrapper<DeliveryRateCard>> {
    return this.http.put<APIResponseWrapper<DeliveryRateCard>>(
      `${this.baseUrl}/${rateId}/contracts/${contractId}/revisions/${revisionId}`,
      rateCard
    );
  }

  delete(rateId: number): Observable<APIResponseWrapper<boolean>> {
    return this.http.delete<APIResponseWrapper<boolean>>(`${this.baseUrl}/${rateId}`);
  }

  template(contractId: number, revisionId: number, format: 'xlsx' | 'csv' = 'xlsx'): Observable<HttpResponse<Blob>> {
    const params = new HttpParams()
      .set('contractId', String(contractId))
      .set('revisionId', String(revisionId))
      .set('format', format);
    return this.http.get(`${this.baseUrl}/template`, { params, observe: 'response', responseType: 'blob' });
  }

  upload(file: File, contractId: number, revisionId: number, owningCompanyId?: number): Observable<APIResponseWrapper<DeliveryRateCardImportPreview>> {
    const form = new FormData();
    form.append('file', file);
    form.append('contractId', String(contractId));
    form.append('revisionId', String(revisionId));
    if (owningCompanyId) form.append('owningCompanyId', String(owningCompanyId));
    return this.http.post<APIResponseWrapper<DeliveryRateCardImportPreview>>(`${this.baseUrl}/import/batches/upload`, form);
  }

  batches(contractId?: number, revisionId?: number, owningCompanyId?: number): Observable<APIListWrapper<DeliveryRateCardImportBatch>> {
    let params = new HttpParams();
    if (contractId) params = params.set('contractId', String(contractId));
    if (revisionId) params = params.set('revisionId', String(revisionId));
    if (owningCompanyId) params = params.set('owningCompanyId', String(owningCompanyId));
    return this.http.get<APIListWrapper<DeliveryRateCardImportBatch>>(`${this.baseUrl}/import/batches`, { params });
  }

  batch(batchId: number): Observable<APIResponseWrapper<DeliveryRateCardImportPreview>> {
    return this.http.get<APIResponseWrapper<DeliveryRateCardImportPreview>>(`${this.baseUrl}/import/batches/${batchId}`);
  }

  updateRow(row: DeliveryRateCardImportRow): Observable<APIResponseWrapper<DeliveryRateCardImportRow>> {
    return this.http.put<APIResponseWrapper<DeliveryRateCardImportRow>>(`${this.baseUrl}/import/rows/${row.rowId}`, {
      values: row.values || {}
    });
  }

  processBatch(batchId: number): Observable<APIResponseWrapper<DeliveryRateCardImportProcessResult>> {
    return this.http.post<APIResponseWrapper<DeliveryRateCardImportProcessResult>>(`${this.baseUrl}/import/batches/${batchId}/process`, {});
  }

  processRow(rowId: number): Observable<APIResponseWrapper<DeliveryRateCardImportRow>> {
    return this.http.post<APIResponseWrapper<DeliveryRateCardImportRow>>(`${this.baseUrl}/import/rows/${rowId}/process`, {});
  }
}
